import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell, PageHeader, Panel, Btn, Tag } from "@/components/Shell";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [settings, setSettings] = useState(user?.org?.settings || {});
  const [locations, setLocations] = useState([]);
  const [taxRates, setTaxRates] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/tax-rates").then((r) => setTaxRates(r.data)).catch(() => {});
    setSettings(user?.org?.settings || {});
  }, [user]);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setSettings(data);
      await refresh();
      toast.success("Settings saved");
      if (patch.operating_mode) {
        const { data: modeRes } = await api.put("/onboarding/mode", { operating_mode: patch.operating_mode });
        (modeRes.warnings || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell>
      <PageHeader title="Settings" subtitle="Fulfilment behaviour, credit-note handling, locations and tax rates." testId="settings-header" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Operating mode" testId="operating-mode-panel" className="lg:col-span-2">
          <div className="space-y-3 p-4 text-sm">
            <p className="text-slate-600">
              Choose how much of Stock360 talks to Xero. Switching mode never rewrites history —
              records already created stay exactly as they are.
            </p>
            <div className="grid gap-px bg-slate-200 md:grid-cols-3">
              {[
                ["full_xero", "Full Xero", "Xero invoices commit stock and purchase orders push bills to Xero."],
                ["xero_purchases", "Xero purchases only", "POs and bills sync to Xero; sales and stock-out are recorded in Stock360 against a sale reference."],
                ["spreadsheet", "Spreadsheet only", "No Xero at all — products, stock, purchases and sales all live in Stock360 and Excel."],
              ].map(([value, label, desc]) => {
                const active = (settings.operating_mode || "full_xero") === value;
                return (
                  <button
                    key={value}
                    data-testid={`operating-mode-${value}`}
                    onClick={() => {
                      if (active) return;
                      const ok = window.confirm(
                        `Switch this organisation to "${label}"?\n\n${desc}\n\nExisting records are left as they are.`
                      );
                      if (ok) save({ operating_mode: value });
                    }}
                    className={`p-4 text-left transition-colors ${active ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
                  >
                    <p className="font-display text-sm font-semibold">{label}</p>
                    <p className={`mt-1 text-xs ${active ? "text-slate-300" : "text-slate-500"}`}>{desc}</p>
                    {active && <span className="mt-2 inline-block bg-white px-2 py-0.5 text-[10px] uppercase tracking-widest text-slate-900">Current</span>}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">Admin only. Sales & Issues appears in the last two modes; Xero Invoices only in Full Xero.</p>
          </div>
        </Panel>

        <Panel title="Fulfilment mode" testId="fulfilment-panel">
          <div className="space-y-3 p-4 text-sm">
            <p className="text-slate-600">
              In <strong>Auto</strong>, On Hand deducts the moment an invoice is authorised — Committed
              is transient but still shown during the commit event and in reports. In <strong>Manual</strong>,
              stock sits in Committed until a warehouse user ticks it as fulfilled.
            </p>
            {["auto", "manual"].map((mode) => (
              <label key={mode} className="flex cursor-pointer items-center gap-3 border border-slate-200 p-3 transition-colors hover:border-slate-400">
                <input
                  type="radio"
                  name="fulfilment"
                  data-testid={`fulfilment-${mode}`}
                  checked={settings.fulfilment_mode === mode}
                  onChange={() => save({ fulfilment_mode: mode })}
                />
                <span className="capitalize">{mode}</span>
                {settings.fulfilment_mode === mode && <Tag tone="green">Active</Tag>}
              </label>
            ))}
          </div>
        </Panel>

        <Panel title="Credit notes" testId="credit-note-panel">
          <div className="space-y-3 p-4 text-sm">
            <p className="text-slate-600">
              Auto-restock puts credited quantities straight back On Hand. Off by default so returns
              can be inspected first.
            </p>
            <label className="flex cursor-pointer items-center gap-3 border border-slate-200 p-3">
              <input
                type="checkbox"
                data-testid="credit-restock-toggle"
                checked={!!settings.credit_note_restock}
                onChange={(e) => save({ credit_note_restock: e.target.checked })}
              />
              Auto-restock on credit note
            </label>
            <p className="text-xs text-slate-500">Currency: GBP only. All money stored in pence.</p>
          </div>
        </Panel>

        <Panel title="Locations" testId="locations-panel">
          <ul className="divide-y divide-slate-100 text-sm">
            {locations.map((l) => (
              <li key={l.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`location-row-${l.code}`}>
                <span>{l.name} <span className="mono text-xs text-slate-400">{l.code}</span></span>
                {l.is_default && <Tag tone="blue">Default</Tag>}
              </li>
            ))}
            {locations.length === 0 && <li className="px-4 py-3 text-slate-500">No locations yet.</li>}
          </ul>
        </Panel>

        <Panel title="Tax rates from Xero" testId="tax-rates-panel">
          <ul className="divide-y divide-slate-100 text-sm">
            {taxRates.map((t) => (
              <li key={t.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`tax-row-${t.type}`}>
                <span>{t.name}</span>
                <span className="num">{t.rate}%</span>
              </li>
            ))}
            {taxRates.length === 0 && <li className="px-4 py-3 text-slate-500">Import from Xero to load tax rates.</li>}
          </ul>
        </Panel>

        <Panel title="Roles" testId="roles-panel" className="lg:col-span-2">
          <div className="grid gap-px bg-slate-200 sm:grid-cols-4">
            {[
              ["Admin", "Everything, including settings and Xero connection"],
              ["Manager", "Products, POs, adjustments, unmatched lines"],
              ["Warehouse", "Adjustments, receipts, fulfilment, stock uploads"],
              ["Read-only", "View inventory, gaps and reports"],
            ].map(([role, desc]) => (
              <div key={role} className="bg-white p-4">
                <p className="font-display text-sm font-semibold">{role}</p>
                <p className="mt-1 text-xs text-slate-500">{desc}</p>
                {user?.role === role.toLowerCase().replace("-", "_") && <Tag tone="green">You</Tag>}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
